import ConfigurationRepoStatic from "@/repositories/configurations/ConfigurationRepoStatic";
import ConfigurationRepoFirebase from "@/repositories/configurations/ConfigurationRepoFirebase";
import ShipPartService from "@/services/ShipPartService";
import { config } from "@/utils/configuration/config";
import { shipPartsElements } from "@/utils/constants";
import store from "@/store/index";
import CalcAttainedEEXIService from "@/services/CalcAttainedEEXIService";
import CalcRequiredEEXIService from "@/services/CalcRequiredEEXIService";
import ShipService from "./ShipService";
import Ship from "@/model/Ship";
import Configuration from "@/model/Configuration";
import EconomicService from "./EconomicService";

// Servicio para tratar todos los casos de uso relacionados con las distintas configuraciones
// que pueden tener los buques
class ConfigurationService {
  private static instance: ConfigurationService;
  private configurationRepo: any;
  private configurations: Map<string, Array<Configuration>>;

  // El constructor instancia el repositorio correspondiente
  private constructor() {
    console.log("ConfigurationService - constructor");

    // Se obtiene el valor de la configuración para saber si hay que utilizar firebase o se
    // van a utilizar datos estáticos de prueba
    if (!config.app.useFirebase) {
      this.configurationRepo = new ConfigurationRepoStatic();
    } else {
      this.configurationRepo = new ConfigurationRepoFirebase();
    }

    this.configurations = new Map<string, Array<Configuration>>();
  }

  public static getInstance(): ConfigurationService {
    if (!ConfigurationService.instance) {
      ConfigurationService.instance = new ConfigurationService();
    }

    return ConfigurationService.instance;
  }

  // Método para obtener las configuraciones de un buque correspondiente del repositorio
  public getConfigurationsForShip(ship) {
    // console.log("ConfigurationService - getConfigurationsForShip", ship.id)

    // Buscamos las configuraciones del buque
    const configurations: Array<Configuration> | undefined = this.configurations.get(ship.id);
    if (!configurations) {
      this.configurationRepo.getConfigurationsForShip(ship, this);
    } else {
      ship.configurations = configurations;
    }
  }

  public addNewConfigurationFromRepo(ship, configurationId, configurationObj) {
    // console.log("ConfigurationService-addNewConfigurationFromRepo", configurationId, configurationObj)
    const configurationElement = this.getConfigurationObjFromRepo(configurationId, configurationObj);

    if (configurationElement.isBase) {
      ship.attainedEEXI = configurationElement.attainedEEXI;
    }
    ship.configurations.push(configurationElement);

    this.configurations.set(ship.id, ship.configurations);

    ShipService.getInstance().calcAttainedFromConfigurations(ship.id);

    ShipPartService.getInstance().getShipPartFromConfiguration(ship, configurationId);

    EconomicService.getInstance().getEconomicData(ship, configurationElement as Configuration);
  }

  public selectedConfiguration(configuration) {
    console.log("Modificada la configuración", configuration);
    store.commit("SET_SHIPS_CURRENT_CONFIGURATION", configuration);
  }

  public modifyConfigurationFromRepo(ship, configurationId, configurationObj) {
    console.log("ConfigurationService-modifyConfigurationFromRepo", ship, configurationId, configurationObj);

    const configurationElement = this.getConfigurationObjFromRepo(configurationId, configurationObj);

    if (configurationElement.isBase) {
      ship.attainedEEXI = configurationElement.attainedEEXI;
    }

    const index = ship.configurations.findIndex((configurations) => configurations.id === configurationId);
    if (index !== -1) {
      ship.configurations[index] = configurationElement;
    }

    this.configurations.set(ship.id, ship.configurations);

    ShipService.getInstance().calcAttainedFromConfigurations(ship.id);

    if (configurationId === store.getters.currentConfiguration.id) {
      const oldObj = store.getters.currentConfiguration;

      shipPartsElements.forEach((configChild) => {
        configurationElement[configChild] = oldObj[configChild];
      });

      configurationElement["economicData"] = oldObj["economicData"];

      console.log("Cambiamos la configuración porque está en edición", configurationElement);
      store.commit("SET_SHIPS_CURRENT_CONFIGURATION", configurationElement);
    }
  }

  public removeConfigurationFromRepo(ship, configurationId) {
    const pos = ship.configurations.map((val) => val.id).indexOf(configurationId);
    if (pos >= 0) {
      ship.configurations.splice(pos, 1);
    }
  }

  public createConfiguration(shipId, name, description, isBase) {
    return this.configurationRepo.createConfiguration(shipId, name, description, isBase);
  }

  public cloneConfiguration(configurationToClone: Configuration, shipId, name, description) {
    const configurationToCreate: any = {
      ...configurationToClone,
    };
    configurationToCreate.name = name;
    configurationToCreate.description = description;
    configurationToCreate.isBase = false;
    delete configurationToCreate.id;

    const errors = this.configurationRepo.cloneConfiguration(shipId, configurationToCreate, configurationToClone, this);

    if (errors === "") {
      console.log("No hay errores en la clonación", configurationToClone);
    } else {
      console.log("Tenemos errores en la clonación", errors);
    }
    return errors;
  }

  public clonedConfiguration(shipId, configurationCreatedId, configurationCreated, configurationToClone) {
    console.log("Vamos a clonar el resto de elementos internos de la configuración");

    shipPartsElements.forEach((configChild) => {
      configurationCreated[configChild] = [];
      configurationToClone[configChild].forEach((shipPart) => {
        console.log("clonando", configChild, shipPart);
        const shipPartElement = {
          ...shipPart,
        };
        delete shipPartElement.id;

        ShipPartService.getInstance().cloneShipPart(shipId, configurationCreatedId, configChild, shipPartElement);
      });
    });

    // Hay que clonar los datos económicos de la configuración
    EconomicService.getInstance().cloneEconomicData(shipId, configurationCreatedId, configurationToClone.economicData);
  }

  public deleteConfiguration(shipId: string, configurationId: string) {
    this.configurationRepo.deleteConfiguration(shipId, configurationId);
  }

  public calcAttainedValue(configuration, ship, parametricTables) {
    console.log("ShipPartService.calcAttainedValue");
    const result = CalcAttainedEEXIService.calcTotalAttainedValue(configuration, ship, parametricTables);
    console.log("ShipPartService.calcAttainedValue Result", configuration.attainedEEXI);
    return result;
  }

  public setConfigurationAsBase(shipId, configurationId, isBase) {
    let errors = "";
    if (isBase) {
      console.log("Estableciendo nueva configuración como base", configurationId);
      const ship: Ship | undefined = ShipService.getInstance().getShip(shipId);

      if (ship) {
        console.log("Hemos encontrado el buque", shipId, "en el servicio, vamos a buscar si ya había una base");
        ship.configurations.forEach((configuration) => {
          if (configuration.id !== configurationId && configuration.isBase) {
            console.log("Hemos encontrado la configuración", configuration.id, "que era base anteriormente");
            errors = this.configurationRepo.setConfigurationAsBase(shipId, configuration.id, false);
          }
        });
      }
    }

    errors = errors + this.configurationRepo.setConfigurationAsBase(shipId, configurationId, isBase);
    return errors;
  }

  public saveConfiguration(shipId: string, configuration: Configuration) {
    console.log("saveConfiguration", shipId, configuration.id, configuration);

    const configurationToSave: any = {
      ...configuration,
    };
    delete configurationToSave.economicData;
    delete configurationToSave.AuxiliaryEngineEmissions;
    delete configurationToSave.AuxiliaryEngineEnergySavings;
    delete configurationToSave.MainEngineEmissions;
    delete configurationToSave.MainEngineEnergySavings;
    delete configurationToSave.PowerTakeIns;

    configurationToSave.modified_gt = configuration.modified_gt == undefined ? null : configuration.modified_gt;
    configurationToSave.modified_dwt = configuration.modified_dwt == undefined ? null : configuration.modified_dwt;

    return this.configurationRepo.saveConfiguration(shipId, configurationToSave);
  }

  public calcRequiredEEXI(ship: Ship, configuration: Configuration) {
    let result: boolean | undefined = undefined;

    if (
      (configuration.modified_dwt && configuration.modified_dwt != 0) ||
      (configuration.modified_gt && configuration.modified_gt != 0)
    ) {
      const { requiredEEXI, logElement } = CalcRequiredEEXIService.calcRequiredEEXI(ship, configuration);

      console.log("RESULT: ", requiredEEXI, logElement);

      if (requiredEEXI) {
        configuration.modified_required_EEXI = requiredEEXI;
        result = true;
      } else {
        configuration.modified_required_EEXI = 0;
        result = false;
      }

      if (logElement.length > 0) {
        configuration.logElement = logElement;
      }
    } else {
      configuration.modified_required_EEXI = null;
    }

    return result;
  }

  private getConfigurationObjFromRepo(configurationId, configurationObjRepo) {
    const configurationElement = {
      id: configurationId,
      name: configurationObjRepo.name,
      description: configurationObjRepo.description,
      attainedEEXI: configurationObjRepo.attainedEEXI,
      isBase: configurationObjRepo.isBase ? configurationObjRepo.isBase : false,
      fj: configurationObjRepo.fj,
      fj_unapply: configurationObjRepo.fj_unapply ? true : false,
      fi: configurationObjRepo.fi,
      fi_unapply: configurationObjRepo.fi_unapply ? true : false,
      fc: configurationObjRepo.fc,
      fc_unapply: configurationObjRepo.fc_unapply ? true : false,
      fl: configurationObjRepo.fl,
      fl_unapply: configurationObjRepo.fl_unapply ? true : false,
      fw: configurationObjRepo.fw,
      fw_unapply: configurationObjRepo.fw_unapply ? true : false,
      fm: configurationObjRepo.fm,
      fm_unapply: configurationObjRepo.fm_unapply ? true : false,
      vref: configurationObjRepo.vref,
      vref_calc: configurationObjRepo.vref_calc,
      modified_gt: configurationObjRepo.modified_gt,
      modified_dwt: configurationObjRepo.modified_dwt,
      modified_required_EEXI: configurationObjRepo.modified_required_EEXI,
      forceVRefCalc: configurationObjRepo.forceVRefCalc ? true : false,
    };

    return configurationElement;
  }
}

export default ConfigurationService;
